import { SendIcon, Square } from 'lucide-react';
import { useMemo, useState } from 'react';

import { AutoResizeTextarea } from '@/shared/components/auto-resize-textarea';
import { Button } from '@/shared/components/shadcn/button';
import { cn } from '@/shared/utils';
import { RingSpinner } from '@/shared/components/loader/ring-spinner';
import { useDiscoveryStore } from '../../stores/discovery-store';

import { Scene, Stage } from '@/shared/components/stage/stage';
import { useSeedPostStore, type SeedPost } from '../../stores/seed-post-store';
import { SeedPostImage } from '../seed-post/seed-post-image';
import { useStopDiscoverySearch } from '../../hooks';
import { useAnalyzeCommonality } from '../../hooks/use-analyze-commonality';
import { useAnalyzeDiscoveryPrompt } from '../../hooks/use-analyze-discovery-prompt';

export const DISCOVERY_CHAT_INPUT_ID = 'discovery-chat-input';

interface Props {
  submitLabel?: string;
  submitWhenEmpty?: boolean;
  canSubmitWithoutText?: boolean;
  emptySubmitLabel?: string;
  interruptionSubmit?: boolean;
  sessionId: string;
}

export function ChatPanelInput({ sessionId }: Props) {
  const { session, isSearching } = useDiscoveryStore();
  const { stopSearch } = useStopDiscoverySearch({
    sessionId,
    searchId: session?.current_search_history_id,
  });
  const { seedPosts, removeSeedPost, clearSeedPosts, replaceSeedPosts } = useSeedPostStore();
  const analyzePromptMutation = useAnalyzeDiscoveryPrompt();
  const analyzeCommonalityMutation = useAnalyzeCommonality();
  const [internalValue, setInternalValue] = useState('');
  const isEmpty = internalValue.trim().length === 0;

  const handleSubmit = async () => {
    if (seedPosts.length > 0) {
      const submittedSeedPosts = seedPosts;
      clearSeedPosts();
      try {
        await analyzeCommonalityMutation.mutateAsync({
          discoverySessionId: sessionId,
          seedPosts: submittedSeedPosts,
        });
      } catch (error) {
        replaceSeedPosts(submittedSeedPosts);
        throw error;
      }
    } else {
      const trimmed = internalValue.trim();
      if (trimmed) {
        setInternalValue('');
        try {
          await analyzePromptMutation.mutateAsync({
            discoverySessionId: sessionId,
            request: {
              message: trimmed,
            },
          });
        } catch (error) {
          setInternalValue(trimmed);
          throw error;
        }
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isSearching) {
        handleStopAndSubmit();
      } else {
        handleSubmit();
      }
    }
  };

  const handleStopAndSubmit = () => {
    stopSearch();
    void handleSubmit();
  };

  const hideInputArea = seedPosts.length > 0;

  const searchDisabled: boolean = useMemo(() => {
    const hasPrompt = !isEmpty;
    const hasSeedPost = seedPosts.length > 0;

    return !hasPrompt && !hasSeedPost;
  }, [isEmpty, seedPosts.length]);

  return (
    <div
      className={cn('border-input bg-background w-full rounded-xl border px-3.5 py-2.5 shadow-sm')}
    >
      {seedPosts.length > 0 && (
        <div className="mb-2.5">
          <SeedPostAttachments
            seedPosts={seedPosts}
            removeSeedPost={removeSeedPost}
            clearSeedDraft={clearSeedPosts}
          />
        </div>
      )}

      {hideInputArea ? null : (
        <div className={cn('min-h-10 opacity-100 transition-opacity', isEmpty && 'opacity-70')}>
          <AutoResizeTextarea
            id={DISCOVERY_CHAT_INPUT_ID}
            value={internalValue}
            onChange={(e) => setInternalValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe a creator"
            className={cn(
              'text-foreground w-full border-0 bg-transparent px-0 py-0 text-sm shadow-none outline-none focus-visible:ring-0',
              'placeholder:text-muted-foreground',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
            maxHeight={132}
            minHeight={40}
          />
        </div>
      )}

      <div
        className={cn('flex items-center justify-between gap-3', hideInputArea ? 'mt-0' : 'mt-2')}
      >
        <div className="flex items-center gap-2">
          <Stage>
            <Scene status={isSearching}>
              <Button
                onClick={handleStopAndSubmit}
                size="sm"
                className={cn('shrink-0 rounded-full')}
                disabled={searchDisabled}
              >
                <Square className="size-3.5 fill-current" />
                Stop and search
              </Button>
            </Scene>
            <Scene default>
              <Button
                onClick={handleSubmit}
                size={'icon'}
                className={cn('size-8 shrink-0 rounded-full')}
                disabled={searchDisabled}
              >
                <SendIcon className="size-3.5" />
              </Button>
            </Scene>
          </Stage>
        </div>
      </div>
    </div>
  );
}

export const SeedPostAttachments = ({
  seedPosts,
  removeSeedPost,
  clearSeedDraft,
  isLoading,
}: {
  seedPosts: SeedPost[];
  removeSeedPost: (postId: string) => void;
  clearSeedDraft: () => void;
  isLoading?: boolean;
}) => (
  <div className="flex flex-col gap-2.5">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-foreground text-xs font-medium">
          {seedPosts.length} post{seedPosts.length > 1 ? 's' : ''} selected
        </p>
        <div className="mt-2">
          <SeedPostImage posts={seedPosts} size="composer" onRemovePost={removeSeedPost} />
        </div>
      </div>
      <Button type="button" variant="ghost" size="sm" onClick={clearSeedDraft} disabled={isLoading}>
        Clear
      </Button>
    </div>

    {isLoading && (
      <div className="flex items-center gap-2 px-0.5">
        <RingSpinner className="size-4" />
        <p className="text-muted-foreground text-xs">Finding matching keywords...</p>
      </div>
    )}
  </div>
);
